/**
 * DTemplate Slides
 * The Home CEO's answers to the questions a seed deck has to answer, one
 * proposition a slide
 */

import React from 'react';
import { CoverSlide } from '@components/press/pitch-deck/slides/cover-slide';
import { HOUSE_H, HOUSE_PATH, HOUSE_W } from '@components/wee/house-mark';
import { FREEZE_FOR_CAPTURE } from '@components/motion/scroll-motion';
import { ProblemPlot } from '@components/press/pitch-deck/slides/dtemplate/problem-plot';
import {
  Accent,
  Callout,
  CardRow,
  Columns,
  Footnote,
  Funnel,
  LogoMap,
  Loop,
  Phases,
  Points,
  Quotes,
  SplitSlide,
  StatementSlide,
  Table,
  TitleSlide,
  mark,
} from '@components/press/pitch-deck/slides/dtemplate/frame';
import { colors } from '@config/colors';

/*
 * June, 2026-10-05: "for each slide in that deck, ask what question it is
 * trying to answer and what the version of that answer is for The Home
 * CEO. The high level to get across is we are a vertically oriented
 * assistant for moms, that by focusing on moms we stand a chance of
 * beating Muse and Instinct, and we need to raise in order to go big and
 * test our hypothesis in the market."
 *
 * The strategy, as June set it that day: the pre-seed ($1–2M) is a battle
 * for market share — the beachhead's moms using The Home CEO as their
 * dominant AI assistant — not for revenue; later, ads in the free tier for
 * purchase wishes, and Premium. So no slide sets a revenue goal for the
 * round, none promises an ad-free product, and the market is counted in
 * households.
 *
 * June, 2026-10-07: the deck follows the pitch notes in
 * docs/deck_inspiration — a seed deck's beats, one proposition a slide —
 * then her edits to it ("take the spirit and only use exact phrasing where
 * asked").
 *
 * June, 2026-10-09 (~/HomeCEO/june_changes.txt, "BEGIN DECK"): the cover
 * adds "Muse for moms"; the problem rests on Menlo Ventures' consumer-AI
 * findings and on what Muse's launch has done to moms; the solution is an
 * AI loop over every wish, with what's coming soon and why it networks;
 * the team is the decks' own team slide; the landscape a plane of logos;
 * go-to-market four channels; the plan a test of seeding through paid
 * acquisition; the money by quarter, in a race for market share in 2027.
 * Then the whole deck read again for one concise, consistent story.
 */

/** Muse and Instinct, June's named rivals, as their own materials put them
 *  (checked 2026-10-05; the figures move weekly, so the slides date them):
 *  - Muse: Meta's personal AI agent, launched 2026-09-08, "built for
 *    everyone"; free, then $20 or $100 a month, per person.
 *  - Instinct (Spear Street Technology): a personal assistant you text or
 *    call; $1B Series C at a $10B valuation (2026-09-28); free to users.
 *  Both are horizontal — built for everyone, one person at a time — and
 *  neither's own materials mention moms, kids or households. */
const RIVALS_GAP = 'Horizontal: built for everyone, one person at a time';


// ── Main deck: the beats in order, one proposition each ─────────────────────

/** 1 · Vision: X for Y to unlock Z, in June's words, exactly, and under it
 *  the analogies investors already hold: "Instinct for moms", and (June,
 *  2026-10-09) "Muse for moms". The mark drawn larger, to balance the page. */
export const DVisionSlide: React.FC = () => (
  <CoverSlide
    subtitle="Specialized AI for moms managing households, learns from helping one mom to improve for the next mom."
    info={
      <>
        <span>“Instinct or Muse for moms.”</span>
      </>
    }
    markEm={3}
  />
);

/** 2 · The world is broken: June's text (2026-10-13) — what a mom's
 *  household work is worth a year, and that accuracy and trust decide who
 *  wins (Menlo Ventures) — stacked beside the plot she asked for that day:
 *  the blueprint's apps and the AI assistants on quality (how well each
 *  does its own job) against generic to specialized, `ProblemPlot`; her
 *  banner on the shortfalls across the foot. */
export const DProblemSlide: React.FC = () => (
  <StatementSlide
    section="Problem"
    question="What’s broken today, and what does it cost her?"
    headline={
      <>
        Moms run households on piecemeal antiquated tools,
        <br />
        generic AI tries, but struggles to help more than it hurts.
      </>
    }
  >
    <Columns $widths="1fr 600px">
      <ProblemPlot />
      <CardRow
        compact
        columns={1}
        cards={[
          {
            figure: '$145K',
            title: 'of household work a year',
            text: 'What a mom’s unpaid work would cost to hire out, across 19 roles. (Insure.com, 2025)',
          },
          {
            figure: 'Accuracy & Trust',
            title: 'top deciding factors in Consumer AI',
            text: 'Trust, not awareness, decides who wins: accuracy 45%, trust 40%, and security and privacy 36% now outrank ease of use 32%. That favors specialized products. (Menlo Ventures, 2026)',
          },
        ]}
      />
    </Columns>
    {/* The quotes, checked 2026-10-09 against their pages. No named critic
        calls Muse a Trojan horse outright; the BBC asks it in an episode
        title. Kalie Robins went viral (308K+ likes) over Meta's AI
        profiling her kids, on Sept 2, six days before Muse launched;
        Futurism names Meta AI. June's source line (2026-10-13) calls her a
        mom using Muse. */}
    <Quotes
      order={4}
      widths="1fr 2fr"
      lead="General and aggressive AI launches validate market interest, but reveal product shortfalls."
      quotes={[
        {
          text: 'Meta’s Muse: Cute AI Agent Or ‘AI Trojan Horse’?',
          source: 'BBC News · AI Decoded, Oct 2026',
        },
        {
          text: 'I have done so much to make sure my kids are safe…the more it kept showing me, I felt more and more like I was failing my kids, and I didn’t realize it.',
          source: 'Kalie Robins, a mom using Muse · Futurism, Sept 2026',
        },
      ]}
    />
  </StatementSlide>
);

/* 3 · Solution, the platform and its loop: `DSolutionSlide`, in
   solution-slide.tsx. */

/** 4 · Solution, the edge: the three kinds the loop improves, against AI
 *  assistants, then what's coming soon (June, 2026-10-09: camps and
 *  birthday hosts, IVF, Push Button Childcare), and why it scales: products
 *  that network. Named (June, 2026-10-14) for what sets us apart: general
 *  AI. The headline June's (2026-10-18), her line break kept. */
export const DEdgeSlide: React.FC = () => (
  <StatementSlide
    section="Solution · the edge over general AI"
    question="Why can’t a general AI assistant do this, and why does it scale?"
    headline={
      <>
        General assistants optimize text interactions.
        <br />
        Ours optimize enriched experiences leading to ongoing support.
      </>
    }
  >
    <Table
      head={['Every wish improves', 'AI assistants', 'The Home CEO', 'Coming soon']}
      widths="0.95fr 1.3fr 1.4fr 0.95fr"
      focusColumn={2}
      rows={[
        [
          'Pre-fetched data',
          mark('part', 'fetched on demand: answers know only what one search finds'),
          mark('yes', 'gathered before she asks: 104,664 attractions for Adventures alone'),
          'Camps and birthday hosts',
        ],
        [
          'Domain expert models',
          mark('no', 'experts impersonated on the fly: hallucinations, or esoteric models overweighted'),
          mark('yes', 'mathematical models coded from papers, with well-sourced parameters and data'),
          'IVF, from the papers worth modeling',
        ],
        [
          'Assistant capabilities',
          mark('part', 'of little use until handed full access to her accounts'),
          mark('yes', 'calendar invites and projects, without taking over her credentials'),
          'Push Button Childcare',
        ],
      ]}
    />
    <Callout>
      <strong>Why it scales:</strong> every wish served improves the whole system, and the products start to network:
      <br />
      <Accent>school calendars → childcare → adventures; commute → car maintenance → car budgeting → car shopping.</Accent>
    </Callout>
  </StatementSlide>
);

/* 5 · The product: `DProductSlide`, in product-slide.tsx. */

/** 6 · Why now: June's headline (2026-10-09), set in the deck's sentence
 *  case; the past, the AI assistants and the now as she framed them; the
 *  extended-thinking point called out under the now; the last phase the
 *  future. The callout names the broad market interest the conclusion
 *  stands on (June, 2026-10-14). */
export const DWhyNowSlide: React.FC = () => (
  <StatementSlide
    long
    section="Why now"
    question="Why is now the moment for this company?"
    headline="In the past, managing a household has been too complex for one product, except now we can build platforms on top of short software to cover the complexity of a household."
  >
    <Phases
      widths="0.95fr 1.3fr 1.7fr 0.8fr"
      phases={[
        {
          when: '2005–2021',
          name: 'B2C built on data',
          items: ['Pinterest boards, Facebook groups, point solution apps', 'Businesses were built around problem-centric thinking'],
        },
        {
          when: '2023–2026',
          name: 'AI assistants',
          items: [
            'Niche businesses solving for user personas, with deep implementations',
            'General assistants solving for all users, with shallow implementations',
            'Muse (Sept 2026) lifted Meta’s stock 6.5% the next day, and 20%+ ($200B+) in two weeks',
          ],
        },
        // June, 2026-10-05: "for the now make it about now we can have a
        // platform of short software serving complex verticals that could
        // not have existed before".
        {
          when: 'Now',
          name: 'A platform of short software',
          items: [
            'Specialized assistants solving for user personas, with deep implementations',
            'The Home CEO: productionized short software, offered at the right time through an assistant whose household context grows with every wish',
          ],
          tone: 'now',
          note: 'With agents’ extended thinking time, the slowest parts of the AI loop have become nearly reliable, so the whole system keeps improving.',
        },
        { when: 'Future', name: 'Consolidation', items: ['Mega apps pull away', 'Consumers cull point solution apps'] },
      ]}
    />
    <Callout>
      Muse and Instinct prove broad market interest, and will pressure moms to join something they don’t want.
      <br />
      <Accent>Now is the time to give them a high-quality, personalized alternative.</Accent>
    </Callout>
    <Footnote>Meta’s stock: daily closes, Sept 2026; Reuters, Sept 22, 2026.</Footnote>
  </StatementSlide>
);

/** 7 · Market, in three tiers: the corner we chose, where it grows, the
 *  whole, in households (Census ACS 2024 microdata, 2026-10-07), with no
 *  capture line; anchored (June, 2026-10-09) on what these households spend
 *  a year, not on what they spend on kids. The corner is the valuable part
 *  of the market the conclusion aims at (June, 2026-10-14). */
export const DMarketSlide: React.FC = () => (
  <StatementSlide
    section="Market"
    question="How big is the market, and where do you start?"
    headline="Moms run the household economy. We start with its most valuable, most demanding corner."
  >
    <CardRow
      cards={[
        {
          figure: '301K',
          title: 'Start: the corner we chose',
          text: 'Dual-income California families with kids, earning $250K+: the most time-starved moms, with the highest bar.',
          tone: 'focus',
        },
        {
          figure: '11.9M',
          title: 'Grow: dual-income families',
          text: 'With kids, earning $100K+, across the US, starting with the 3.2M earning $250K+.',
        },
        {
          figure: '33M',
          title: 'The whole: families with kids',
          text: 'Families with kids under 18, in the US alone. Then families everywhere.',
        },
      ]}
    />
    <Callout order={4}>
      If we win, <Accent>The Home CEO becomes every mom’s household assistant</Accent>, in a household economy that spends
      $2.6T a year.
    </Callout>
    <Footnote>
      Households: Census ACS 2024 microdata · families with kids: FRED, 2024 · spending: everything these households
      spend, BLS Consumer Expenditure Survey, 2024 · the math by tier is in the appendix.
    </Footnote>
  </StatementSlide>
);

/* 8 · Team: `DTeamSlide`, in team-slide.tsx. */

/** Our point on the landscape: the mark itself. */
const HouseLogo: React.FC = () => (
  <svg viewBox={`0 0 ${HOUSE_W} ${HOUSE_H}`} aria-hidden="true" focusable="false">
    <path d={HOUSE_PATH} fill={colors.brand.exxxtraDark} />
  </svg>
);

/** A landscape logo, from public/press/landscape (fetched 2026-10-09). */
const logo = (slug: string, name: string) => <img src={`/press/landscape/${slug}.png`} alt={`${name} logo`} />;

/** 9 · Landscape: June (2026-10-09): "a 2D space with the axis labeled,
 *  use logos with an overlay that opens if clicked" — the session's axes,
 *  built for everyone or for moms, every player named, and not alone in
 *  our corner. The other axis (June, 2026-10-18) is the interface: text
 *  (chat, voice, a terminal) to enriched (screens built for the job), each
 *  player placed by how it's used. */
export const DLandscapeSlide: React.FC = () => (
  <StatementSlide
    section="Landscape"
    question="Who else is going after this, and how are you different?"
    headline="Muse and Instinct prove the demand. We take the corner they aren’t built for: moms."
  >
    <Columns $widths="1.55fr 1fr">
      <LogoMap
        width={1020}
        height={560}
        x={['Built for everyone', 'Built for moms and households']}
        y={['Text UI', 'Enriched UI']}
        points={[
          {
            name: 'The Home CEO',
            x: 0.9,
            y: 0.88,
            focus: true,
            logo: <HouseLogo />,
            about: 'Specialized AI for moms managing households: four products live, with Push Button Childcare and School Calendar next.',
          },
          {
            name: 'Ohai',
            x: 0.74,
            y: 0.3,
            logo: logo('ohai', 'Ohai'),
            about: 'An AI household assistant for parents, from Care.com’s founder: built for families, and just ask.',
          },
          {
            name: 'Muse (Meta)',
            x: 0.18,
            y: 0.2,
            logo: logo('muse', 'Muse'),
            about: 'Meta’s personal AI agent, launched Sept 8, 2026: built for everyone, one person at a time, with Meta’s distribution.',
          },
          {
            name: 'Instinct',
            x: 0.32,
            y: 0.08,
            logo: logo('instinct', 'Instinct'),
            about: 'A personal assistant you text or call; raised $1B at a $10B valuation on Sept 28, 2026. Built for everyone.',
          },
          {
            name: 'ChatGPT',
            x: 0.05,
            y: 0.32,
            logo: logo('chatgpt', 'ChatGPT'),
            about: 'OpenAI’s general assistant: answers for everyone, now adding personal agents.',
          },
          {
            name: 'Gemini',
            x: 0.3,
            y: 0.36,
            logo: logo('gemini', 'Gemini'),
            about: 'Google’s general assistant, across Search, Android and Workspace.',
          },
          {
            name: 'Siri',
            x: 0.45,
            y: 0.22,
            logo: logo('siri', 'Siri'),
            about: 'Apple’s assistant on every iPhone: built for everyone.',
          },
          {
            name: 'OpenClaw',
            x: 0.05,
            y: 0.05,
            logo: logo('openclaw', 'OpenClaw'),
            about: 'An open-source personal agent: powerful, behind a terminal, API keys and setup.',
          },
          {
            name: 'Notion',
            x: 0.12,
            y: 0.72,
            logo: logo('notion', 'Notion'),
            about: 'A workspace she builds herself: flexible, and all her own work.',
          },
          {
            name: 'Google Calendar',
            x: 0.34,
            y: 0.8,
            logo: logo('google-calendar', 'Google Calendar'),
            about: 'The calendar most households share; she still does the planning.',
          },
          {
            name: 'Cozi',
            x: 0.72,
            y: 0.6,
            logo: logo('cozi', 'Cozi'),
            about: 'A family organizer: shared calendars and lists she fills in herself.',
          },
          {
            name: 'Care.com',
            x: 0.62,
            y: 0.78,
            logo: logo('carecom', 'Care.com'),
            about: 'A marketplace for care: one slice of the household, booked by hand.',
          },
          {
            name: 'ClassDojo',
            x: 0.86,
            y: 0.62,
            logo: logo('classdojo', 'ClassDojo'),
            about: 'School-to-parent messaging: one slice of the household.',
          },
          {
            name: 'Thumbtack',
            x: 0.5,
            y: 0.66,
            logo: logo('thumbtack', 'Thumbtack'),
            about: 'A marketplace for home services, booked job by job.',
          },
        ]}
      />
      <Points
        items={[
          {
            lead: 'Validation:',
            text: 'Meta launched Muse on Sept 8, 2026; Instinct raised $1B at a $10B valuation on Sept 28.',
          },
          {
            lead: 'Our difference:',
            text: 'they’re built for everyone, one person at a time, and reactive. We’re built for a mom and her household, and proactive.',
          },
          { lead: 'If they come for moms,', text: 'it proves the market, and focus is how we win it.' },
        ]}
      />
    </Columns>
    {!FREEZE_FOR_CAPTURE && <Footnote>Click a logo for more.</Footnote>}
  </StatementSlide>
);

/* 10 · Go-to-market, the round into the channels and virality back round:
   `DGtmSlide`, in gtm-slide.tsx. */

/** 11 · The plan: June (2026-10-09): centered on market share among the
 *  beachhead's moms, and on one question — can paid acquisition seed enough
 *  of them to make the product sticky and start network effects inside
 *  their mom networks? Seeding is where the CAC the conclusion calls coming
 *  soon is first measured (June, 2026-10-14). */
export const DPlanSlide: React.FC = () => (
  <StatementSlide
    section="Plan · the first test"
    question="What will you test first, and how will you know?"
    headline="First test: can paid acquisition seed enough moms to make the product sticky, and start network effects inside their mom networks?"
  >
    <Columns $widths="1fr 1.1fr">
      <Funnel
        stages={[
          { figure: '301K', text: 'beachhead households: our market' },
          { figure: 'Seed', text: 'a bandit across five paid channels: our first CAC' },
          { figure: 'Stick', text: 'weekly use, wish after wish' },
          { figure: '2%', text: 'market share: 10K weekly active households, growing through mom networks', tone: 'focus' },
        ]}
      />
      <Points
        items={[
          { lead: 'Already running:', text: 'four products live on the App Store and Google Play.' },
          {
            lead: 'Why it can work:',
            text: 'the wishes moms repeat weekly need another mom there, so each seeded mom can pull in her network.',
          },
          {
            lead: 'How we’ll know:',
            text: 'share of the beachhead active weekly; repeat wishes; invites and organic sign-ups inside mom networks; CAC by channel, falling.',
          },
        ]}
      />
    </Columns>
  </StatementSlide>
);

/** 12 · The money: June (2026-10-09): what it buys as metrics, the
 *  quarters with their share of ad spend, and 2027 as a race for market
 *  share. */
export const DFundraiseSlide: React.FC = () => (
  <StatementSlide
    section="Fundraise"
    question="How much, what does it buy, and how will we know it worked?"
    headline="Raising $1–2M pre-seed to win the beachhead: The Home CEO as its moms’ dominant AI assistant."
  >
    <CardRow
      cards={[
        { figure: '4', title: 'people: June and three hires', text: 'Two AI engineers and a community manager' },
        { figure: '100K', title: 'users’ worth of compute', text: 'Well past the first test’s 10K target' },
        { figure: '$100–500K', title: 'of acquisition', text: 'Spent by quarter, on what works', tone: 'focus' },
      ]}
    />
    <Phases
      phases={[
        {
          when: 'Q1 · 10% of ad spend',
          name: 'Baking',
          items: ['Team hired; every system baking, gathering early feedback', 'Product offerings keep expanding'],
          tone: 'now',
        },
        {
          when: 'Q2 · 20%',
          name: 'Double down',
          items: ['More of Q1’s wins; keep learning', 'Products expand in step with what works in marketing'],
        },
        {
          when: 'Q3 · 40%',
          name: 'Back to school',
          items: ['Expand the virality levers', 'Grow through product partnerships'],
        },
        { when: 'Q4 · the last 30%', name: 'Follow the data', items: ['Spend where Q1–Q3 proved out'] },
      ]}
    />
    <Callout order={6}>
      2027 is a race for market share.
      <br />
      <Accent>Every extra resource goes to growing users, and to keeping brand love high with the moms who join.</Accent>
    </Callout>
  </StatementSlide>
);

// ── Appendix: answers to the questions that are coming ───────────────────────

export const DAppendixSlide: React.FC = () => (
  <TitleSlide title="Appendix" subtitle="Answers to the questions we expect, one slide each." />
);

/** The growth flywheel beside the Solution's AI loop, and what a later
 *  rival would have to rebuild. Not called a moat: none is shown yet. */
export const DFlywheelSlide: React.FC = () => (
  <SplitSlide
    section="Flywheel"
    question="What keeps a bigger rival from catching up?"
    headline="Every wish deepens what a rival can’t copy quickly: her household’s memory, and moms’ trust."
    points={[
      <>
        <strong>Household memory:</strong> the more of her household it knows, the more starting over elsewhere costs her.
      </>,
      <>
        <strong>Trust:</strong> an assistant moms recommend to moms, free to start, and led with kindness.
      </>,
      <>
        <strong>A head start on every wish:</strong> the data and models behind each one were built on the wishes
        before, so a rival starts behind on all of them.
      </>,
    ]}
    picture={
      <Loop
        centre="Household memory"
        steps={['A mom’s wish', 'A better answer', 'Moms tell moms', 'More moms, more wishes']}
      />
    }
  />
);

/** The wishes after the live four, the solution's "coming soon" among them. */
export const DOtherExamplesSlide: React.FC = () => (
  <StatementSlide
    section="Next wishes"
    question="What comes after the four that are live?"
    headline="The next wishes are already lined up."
  >
    <CardRow
      columns={4}
      cards={[
        {
          title: 'Push Button Childcare',
          text: '“Who can watch the kids on a sick day?” Care booked with the sitters she already knows and trusts, texted, with no app for them to install.',
          tone: 'focus',
        },
        {
          title: 'School Calendar',
          text: 'Every school calendar and closure in one place, so no day off school takes her by surprise.',
          tone: 'focus',
        },
        {
          title: 'Camps and birthday hosts',
          text: 'The camps and party hosts near her, pre-fetched and compared before she asks.',
        },
        {
          title: 'IVF',
          text: 'Mathematical models coded from the papers worth modeling, with well-sourced parameters.',
        },
      ]}
    />
    <Callout order={6}>
      Also lined up: date nights, playdates and partner tasks. <Accent>And many more, one wish at a time.</Accent>
    </Callout>
  </StatementSlide>
);

/** The session's "what makes you think moms will pick another app?", and
 *  June's answer there: past the command line and the chat window, to
 *  screens built for each job. */
export const DWhyAnotherAppSlide: React.FC = () => (
  <StatementSlide
    section="Why another app"
    question="What makes you think moms will pick another app?"
    headline="It isn’t another chat window: every wish gets screens built for the job."
  >
    <Phases
      phases={[
        {
          when: 'Technical',
          name: 'A command line',
          items: ['OpenClaw and other open-source agents', 'Terminals, API keys, thinking traces'],
        },
        { when: 'General', name: 'A chat window', items: ['ChatGPT, Gemini, Muse', 'Answers in a thread'] },
        {
          when: 'The Home CEO',
          name: 'Built for the job',
          items: ['A Saturday planned stop by stop; growth on a chart', 'Commutes side by side; sizes across brands'],
          tone: 'now',
        },
      ]}
    />
    <Callout>
      She doesn’t need another AI telling her what to do.{' '}
      <Accent>She needs the wish done, shown the way she’ll use it.</Accent>
    </Callout>
  </StatementSlide>
);

/** The market math for each tier the Market slide claims, in households,
 *  read from the whole down. Counted afresh 2026-10-07 from the Census's
 *  2024 ACS 1-year microdata (PUMS household files): households where the
 *  householder and spouse are both employed (WORKSTAT 01), with their own
 *  children under 18 (HUPAOC 1–3), at household income (HINCP × ADJINC)
 *  of $100K or $250K and up, by the housing weight; 90% margins from the
 *  80 replicate weights. Same-sex married couples aren't coded in
 *  WORKSTAT, so aren't counted. */
export const DMarketMathSlide: React.FC = () => (
  <StatementSlide
    section="Market math"
    question="How did you count each tier?"
    headline="Every tier counted in households, from the Census’s own 2024 microdata."
  >
    <Table
      head={['Tier', 'Households', 'Who', 'Counted from']}
      widths="0.7fr 1fr 1.6fr 1.9fr"
      focusRow={3}
      rows={[
        ['The whole', '33.3M', 'US families with kids under 18', 'FRED, 2024'],
        ['Grow', '11.89M ± 0.07M', 'Dual-income married couples with kids, $100K+', 'ACS 2024 microdata, US'],
        ['Then', '3.16M ± 0.03M', 'The same, at $250K+', 'ACS 2024 microdata, US'],
        ['Start', '498K ± 12K', 'The same, in California', 'ACS 2024 microdata, California'],
      ]}
    />
    <Footnote>
      Dual-income: both spouses employed. Kids: their own, under 18. Income: the household’s, in 2024 dollars. Margins are
      the Census’s 90% intervals. FRED series TTLFMCU.
    </Footnote>
  </StatementSlide>
);

/** The session's "why dual-income moms?", which it sent here, out of the
 *  Market slide. The second card is June's own reason. */
export const DDualIncomeSlide: React.FC = () => (
  <StatementSlide
    section="Why dual-income moms"
    question="Why start with dual-income moms?"
    headline="They have the least time, and they already run the household like a business."
  >
    <CardRow
      cards={[
        {
          figure: '73.9%',
          title: 'of US moms are in the workforce',
          text: 'Moms of kids under 18. In a dual-income home, the household runs in the margins of two careers. (BLS, 2025)',
        },
        {
          title: 'They run it like a business',
          text: 'Two careers train a household to plan, delegate and pay for help: the way a mom uses The Home CEO, wish by wish.',
          tone: 'focus',
        },
        {
          title: 'They set the bar',
          text: 'The most demanding moms to win. An assistant good enough for them is good enough for the rest.',
        },
      ]}
    />
  </StatementSlide>
);

/** Who uses it, who pays, and the job each hires it for. */
export const DPersonasSlide: React.FC = () => (
  <StatementSlide
    section="Personas and jobs to be done"
    question="Who uses it, who pays, and what job do they hire it for?"
    headline="Built for the mom who runs the household, and the people she coordinates."
  >
    <Table
      head={['Persona', 'Job', 'Current solution', 'Current pain']}
      widths="1.05fr 1.3fr 1.3fr 1.5fr"
      focusRow={0}
      rows={[
        [
          'Mom · user, later Premium’s buyer',
          'Run the household: plans, health, schedules and logistics',
          'Calendars, lists, group chats, point solution apps, generic AI',
          'Carries the mental load: 5+ hours a week, against about 2 for dads',
        ],
        [
          'Partner · user',
          'Help without having to ask',
          '“What do you need me to do?”',
          'The know-how stays with mom; dads overestimate their share 2×',
        ],
        [
          'Sitters and family · in the loop',
          'Cover care when plans change',
          'Texts and calls',
          'Last-minute scrambles, and another app to install',
        ],
        [
          'Kids · who it’s all for',
          'Grow, play, get where they’re going',
          'Whatever mom has time to find',
          'Generic answers: “Try the library or a park.”',
        ],
        [
          'Household brands · advertisers, later',
          'Reach a mom as she decides what to buy',
          'Search, social and retail ads',
          'Far from the moment she’s choosing',
        ],
      ]}
    />
    <Footnote>Petts & Carlson, 2023 · Daminger, 2019 · the generic answer is How We Build’s level 1.</Footnote>
  </StatementSlide>
);

/** The question every consumer-AI pitch gets, and the session's answer:
 *  we hope they try, it proves the market; here is why focus wins the
 *  corner we're taking. Muse is the one to beat, and the slide says so. */
export const DWhyNotRivalsSlide: React.FC = () => (
  <StatementSlide
    section="Why not them"
    question="Why won’t Muse, Instinct or OpenAI just do this?"
    headline="We hope they try: it proves the market. Depth for one customer beats breadth for everyone."
  >
    <Table
      head={['Market', 'Companies', 'Gap', 'The Home CEO']}
      widths="1fr 1.05fr 1.45fr 1.5fr"
      focusColumn={3}
      rows={[
        [
          'Personal AI agents',
          'Muse (Meta), Instinct',
          RIVALS_GAP,
          'Only moms: her household’s context, and invites made without her credentials',
        ],
        [
          'General AI assistants',
          'ChatGPT, Gemini',
          'Reactive: data fetched on demand, experts impersonated on the fly',
          'Pre-fetched data and mathematical models, built once for each kind of wish',
        ],
        ['Family organizers', 'Cozi, shared calendars', 'Tools for mom to do the planning herself', 'The research, the logistics and the follow-through, done'],
        [
          'Point solution apps',
          'Care.com, Thumbtack, UrbanSitter, ClassDojo',
          'One slice each, every service another thing to manage',
          'One assistant across the household, free for every mom',
        ],
      ]}
    />
    <Callout order={4}>
      Muse is the one to beat: it has the distribution where moms already are.{' '}
      <Accent>Its bet is everyone; ours is moms.</Accent>
    </Callout>
  </StatementSlide>
);

/** The session's "why is this more viral than a normal assistant app?":
 *  the wishes moms repeat weekly need someone else there, which is the
 *  go-to-market's local virality. None of these is live yet, so each says
 *  when. */
export const DViralSlide: React.FC = () => (
  <StatementSlide
    section="Virality"
    question="Why is it more viral than a normal assistant?"
    headline="The wishes moms repeat every week need someone else there."
  >
    <CardRow
      columns={4}
      cards={[
        {
          title: 'Push Button Childcare · next',
          text: 'The sitters she already knows and trusts, booked by text, no app needed; and sitters tell other families.',
          tone: 'focus',
        },
        {
          title: 'School Calendar · next',
          text: 'School calendars shared with the other parents, so a day off surprises no one.',
          tone: 'focus',
        },
        {
          title: 'Playdates · then',
          text: 'A time, a place and the other mom invited, in one wish.',
        },
        {
          title: 'Date nights · then',
          text: 'The sitter, the reservation and the calendar, together.',
        },
      ]}
    />
    <Callout order={6}>
      A normal assistant serves one person. <Accent>Hers is built around the people she plans with.</Accent>
    </Callout>
  </StatementSlide>
);

/** How it makes money, after the pre-seed. */
export const DBusinessModelSlide: React.FC = () => (
  <StatementSlide
    section="Business model"
    question="How do you make money, and how big can it get?"
    headline="Free for every mom first. Then two ways to earn: ads in purchase wishes, and Premium."
  >
    <CardRow
      cards={[
        {
          figure: 'Free',
          title: 'for every mom',
          text: 'The pre-seed is a race for market share, not revenue: to be the beachhead’s dominant AI assistant.',
          tone: 'focus',
        },
        {
          figure: 'Ads',
          title: 'in purchase wishes, in the free tier',
          text: 'Only where a wish is about buying for the household (kids’ clothes, home services, date nights), at the moment she’s already choosing.',
        },
        {
          figure: 'Premium',
          title: 'for the expensive deep research',
          text: 'Hiring for household tasks, unlimited wishes, and more.',
        },
      ]}
    />
    <Callout order={5}>
      The size of the prize: these households spend <strong>$2.6T a year</strong>, and purchase wishes sit right beside
      that spending.
    </Callout>
    <Footnote>$2.6T: everything married couples and single parents with kids under 18 spend, BLS Consumer Expenditure Survey, 2024.</Footnote>
  </StatementSlide>
);

/** The platform's layers and when each product comes. */
export const DRoadmapSlide: React.FC = () => (
  <StatementSlide
    section="Product layers and timing"
    question="What’s built now, and what comes next?"
    headline="One platform in layers, with products on top."
  >
    <Phases
      phases={[
        {
          when: 'Now · live',
          name: 'Four products',
          items: [
            'Wish, Explore, Enact',
            'Adventures, Growth Curves, Commute Optimizer, Kids Clothes',
            'iOS and Android',
          ],
          tone: 'now',
        },
        {
          when: 'Next',
          name: 'Shipping',
          items: [
            'Push Button Childcare: sitters she knows and trusts',
            'School Calendar: no surprise days off',
            'Invites: another mom in every weekly wish',
          ],
        },
        {
          when: 'Then',
          name: 'Complete the coverage',
          items: ['Camps and birthday hosts; IVF', 'Playdates, date nights, partner tasks'],
        },
        {
          when: 'After the pre-seed',
          name: 'Market maker',
          items: [
            'Ads in the free tier, in wishes around household purchases',
            'Premium: deep research like hiring for household tasks, unlimited wishes',
            'Beyond the beachhead: 3.2M US households',
          ],
        },
      ]}
    />
    <Callout>
      The layers under every product: <strong>household memory</strong> · <strong>pre-fetched data</strong> ·{' '}
      <strong>domain expert models</strong> · <strong>assistant capabilities</strong> (calendar invites, projects,
      sharing).
    </Callout>
  </StatementSlide>
);

export const DClosingSlide: React.FC = () => (
  <TitleSlide
    title="The Home CEO"
    subtitle="Specialized AI for moms managing households. Every wish makes it better for the next mom."
    note="DTemplate · a draft: each coral question is one an investor asks, and each slide our answer."
  />
);
